import React from 'react'
import PropTypes from 'prop-types'
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import { GatsbyImage } from 'gatsby-plugin-image'

// Styles
import {
  Super,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Title,
  TextBody,
} from '../TextStyles'
import AnimateSplitText from '../animation/AnimateSplitText'
import AnimateScaleIn from '../animation/AnimateScaleIn'
import Button from '../Button'
import Table from '../Table'
import {
  ListItem,
  OrderedList,
  RichTextMain,
  UnorderedList,
} from './index.style'
import { animation } from '../../styles/vars/animation.style'
import { colors } from '../../styles/vars/colors.style'

const getBlockDuration = content => {
  let wordLength = 0

  content.forEach(firstChild => {
    if (firstChild.length) {
      firstChild.forEach(secondChild => {
        if (typeof secondChild === 'string') {
          wordLength += secondChild.split(' ').length
        } else {
          secondChild.props.children.forEach(thirdChild => {
            if (typeof thirdChild === 'string') {
              wordLength += thirdChild.split(' ').length
            }
          })
        }
      })
    } else {
      if (typeof firstChild.type === 'function') {
        wordLength += firstChild.props.children?.props.children[0][0].split(' ')
          .length
      } else {
        const child = firstChild.props.children[0]

        if (typeof child === 'string') {
          wordLength += child.split(' ').length
        } else if (child.length) {
          child.forEach(item => {
            wordLength += item.split(' ').length
          })
        }
      }
    }
  })

  return wordLength * animation.lineDuration
}

const RichText = ({
  id,
  content,
  delay,
  delayEm,
  forceHeading,
  superHeading,
  upgradeHeading,
  hasHeadingOne,
  button,
  useInView,
  isInView,
  debug,
}) => {
  let blockDurations = [0]

  const options = {
    renderMark: {
      [MARKS.BOLD]: text => <strong>{text}</strong>,
      [MARKS.ITALIC]: text => (
        <span className={'em'} style={{ display: 'inline-block' }}>
          {text}
        </span>
      ),
      [MARKS.CODE]: text => <i className={'sup'}>{text}</i>,
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => {
        if (children[0].length === 1 && children[0][0] === '') return null

        const paragraphId = `${id}${children[0][0]
          .substring(1, 20)
          .replace(/[^0-9a-z]/gi, '')}`

        blockDurations.push(getBlockDuration(children))

        return (
          <TextBody>
            <AnimateSplitText
              id={paragraphId}
              delay={delay}
              useInView={useInView}
              isInView={isInView}
              debug={debug}
            >
              {children}
            </AnimateSplitText>
          </TextBody>
        )
      },

      [BLOCKS.HEADING_1]: (node, children) => {
        const blockDelay = blockDurations.reduce(
          (accumulator, value) => accumulator + value
        )

        blockDurations.push(getBlockDuration(children))

        if (superHeading) {
          return (
            <Super as={forceHeading ? `h1` : ``}>
              <AnimateSplitText
                id={id}
                delay={delay + blockDelay}
                debug={debug}
                useInView={useInView}
                isInView={isInView}
              >
                {children}
              </AnimateSplitText>
            </Super>
          )
        } else {
          return (
            <Heading1 as={forceHeading ? `h1` : ``}>
              <AnimateSplitText
                id={id}
                delay={delay + blockDelay}
                delayEm={delayEm}
                debug={debug}
                useInView={useInView}
                isInView={isInView}
              >
                {children}
              </AnimateSplitText>
            </Heading1>
          )
        }
      },

      [BLOCKS.HEADING_2]: (node, children) => {
        const blockDelay = blockDurations.reduce(
          (accumulator, value) => accumulator + value
        )

        blockDurations.push(getBlockDuration(children))

        if (upgradeHeading) {
          return (
            <Heading1
              as={
                forceHeading && hasHeadingOne ? `h1` : forceHeading ? `h2` : ``
              }
            >
              <AnimateSplitText
                id={id}
                delay={delay + blockDelay}
                delayEm={delayEm}
                debug={debug}
                useInView={useInView}
                isInView={isInView}
              >
                {children}
              </AnimateSplitText>
            </Heading1>
          )
        } else {
          return (
            <Heading2
              as={
                forceHeading && hasHeadingOne ? `h1` : forceHeading ? `h2` : ``
              }
            >
              <AnimateSplitText
                id={id}
                delay={delay + blockDelay}
                delayEm={delayEm}
                debug={debug}
                useInView={useInView}
                isInView={isInView}
              >
                {children}
              </AnimateSplitText>
            </Heading2>
          )
        }
      },

      [BLOCKS.HEADING_3]: (node, children) => {
        const blockDelay = blockDurations.reduce(
          (accumulator, value) => accumulator + value
        )

        blockDurations.push(getBlockDuration(children))

        if (upgradeHeading) {
          return (
            <Heading2
              as={
                forceHeading && hasHeadingOne ? `h1` : forceHeading ? `h2` : ``
              }
            >
              <AnimateSplitText
                id={id}
                delay={delay + blockDelay}
                delayEm={delayEm}
                debug={debug}
                useInView={useInView}
                isInView={isInView}
              >
                {children}
              </AnimateSplitText>
            </Heading2>
          )
        } else {
          return (
            <Heading3
              as={
                forceHeading && hasHeadingOne ? `h1` : forceHeading ? `h2` : ``
              }
            >
              <AnimateSplitText
                id={id}
                delay={delay + blockDelay}
                delayEm={delayEm}
                debug={debug}
                useInView={useInView}
                isInView={isInView}
              >
                {children}
              </AnimateSplitText>
            </Heading3>
          )
        }
      },

      [BLOCKS.HEADING_4]: (node, children) => {
        const blockDelay = blockDurations.reduce(
          (accumulator, value) => accumulator + value
        )

        blockDurations.push(getBlockDuration(children))

        return (
          <Heading4 as={forceHeading ? `h4` : ``}>
            <AnimateSplitText
              id={id}
              delay={delay + blockDelay}
              delayEm={delayEm}
              useInView={useInView}
              isInView={isInView}
            >
              {children}
            </AnimateSplitText>
          </Heading4>
        )
      },

      [BLOCKS.HEADING_5]: (node, children) => {
        const blockDelay = blockDurations.reduce(
          (accumulator, value) => accumulator + value
        )

        blockDurations.push(getBlockDuration(children))

        return (
          <Title>
            <AnimateSplitText
              id={id}
              delay={delay + blockDelay}
              delayEm={delayEm}
              useInView={useInView}
              isInView={isInView}
            >
              {children}
            </AnimateSplitText>
          </Title>
        )
      },

      [BLOCKS.UL_LIST]: (node, children) => (
        <UnorderedList>{children}</UnorderedList>
      ),

      [BLOCKS.OL_LIST]: (node, children) => (
        <OrderedList>{children}</OrderedList>
      ),

      [BLOCKS.LIST_ITEM]: (node, children) => {
        const blockDelay = blockDurations.reduce(
          (accumulator, value) => accumulator + value
        )

        blockDurations.push(getBlockDuration(children))

        return <ListItem delay={delay + blockDelay}>{children}</ListItem>
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
        return <Table content={node.data.target.table.tableData} />
      },
      [BLOCKS.EMBEDDED_ASSET]: (node, children) => {
        const { description, gatsbyImageData, contentful_id } = node.data.target

        return (
          <AnimateScaleIn
            id={contentful_id}
            position="relative"
            borderRadius={true}
          >
            <GatsbyImage
              image={gatsbyImageData}
              alt={description ? description : ''}
            />
          </AnimateScaleIn>
        )
      },
      [INLINES.HYPERLINK]: (node, children) => {
        if (button) {
          return (
            <Button href={node.data.uri} color={colors.light} theme="outline">
              {node.content[0].value}
            </Button>
          )
        } else {
          return (
            <a href={node.data.uri} target="_blank" rel="noreferrer">
              {children}
            </a>
          )
        }
      },
    },
    renderText: text =>
      React.Children.toArray(
        text.split('\n').map((t, i) =>
          i > 0 ? (
            <>
              <br />
              {t}
            </>
          ) : (
            t
          )
        )
      ),
  }

  return <RichTextMain>{renderRichText(content, options)}</RichTextMain>
}

RichText.propTypes = {
  id: PropTypes.string.isRequired,
  content: PropTypes.object,
  delay: PropTypes.number,
  delayEm: PropTypes.bool,
  forceHeading: PropTypes.bool,
  superHeading: PropTypes.bool,
  upgradeHeading: PropTypes.bool,
  button: PropTypes.bool,
  useInView: PropTypes.bool,
  isInView: PropTypes.bool,
}

RichText.defaultProps = {
  delay: 0,
}

export default RichText
